/**
 * surgicalTempoModule.js - 2026-10-18 (Tab 3 review, Dr. Tatar: shift mode and fatigue linked to surge and MASCAL)
 *
 * The operations tempo of the surgical teams of one facility (Role 2 Basic first) for one day:
 *   - the tempo wanted follows the LOAD: demand of the day plus the backlog against the normal capacity of the teams
 *     (above normal: surge; above the surge capacity: crisis) and the MASCAL level (MASCAL 1: at least surge; MASCAL 2 or
 *     more: crisis);
 *   - the planner's policy caps it (the highest tempo allowed);
 *   - the surgeon performance is the average of the fatigue curve over the shift length of that tempo, lowered by sleep debt
 *     after more days than the team can keep that tempo (days on before rest), and recovered by normal days;
 *   - the performance gives the capacity of the day, and a died-of-wounds factor (+2 percent per 10 percent lost).
 * Constants, their status and sources: constants/surgicalTempo.js.
 */
import { SURGICAL_FATIGUE_MODEL } from '../../constants/surgicalCapacity.js';
import {
  SURGICAL_TEMPOS,
  TEMPO_RECOMMENDATION_KEY,
  TEMPO_CAPACITY_RATIO,
  LOAD_RATIO_SURGE_FROM,
  LOAD_RATIO_CRISIS_FROM,
  SLEEP_DEBT_LOSS_PER_DAY,
  SLEEP_DEBT_FLOOR,
  NORMAL_DAY_RECOVERS_DAYS,
  FATIGUE_MORTALITY_PER_10_PERCENT,
  DEFAULT_MAX_TEMPO,
  averageShiftPerformance
} from '../../constants/surgicalTempo.js';

const RANK = { NORMAL: 0, SURGE: 1, CRISIS: 2 };
// Days of non-normal tempo after which the sleep debt has reached its floor.
const MAX_DEBT_DAYS = SURGICAL_FATIGUE_MODEL.SHIFT_RECOMMENDATIONS.surge_operations.days_on_before_rest +
  Math.ceil((1 - SLEEP_DEBT_FLOOR) / SLEEP_DEBT_LOSS_PER_DAY);

/** Tempo that a MASCAL level asks for (levels of assessMASCALCondition and of the system state). */
export function tempoOfMascalLevel(level) {
  if (!level || level === 'NORMAL' || level === 'ELEVATED') return 'NORMAL';
  if (/(^|_)1$/.test(level) || level === 'LEVEL_1' || level === 'MASCAL_1') return 'SURGE';
  if (/(^|_)[23]$/.test(level) || level === 'LEVEL_2' || level === 'LEVEL_3' || level === 'MASCAL_2' || level === 'MASCAL_3') return 'CRISIS';
  return 'NORMAL';
}

/**
 * @param {Object} p
 * @param {number} p.demandCases        surgical cases of the day
 * @param {number} p.backlog            cases waiting from the day before
 * @param {number} p.teams              surgical teams of the facility (team equivalents)
 * @param {number} p.normalCasesPerTeam cases a team does in a day at the normal tempo
 * @param {string} [p.mascalLevel]      MASCAL level of the day
 * @param {string} [p.maxTempo]         highest tempo the planner allows
 * @param {Object} [p.state]            { non_normal_days } from the day before
 */
export function deriveSurgicalTempo({ demandCases, backlog, teams, normalCasesPerTeam, mascalLevel, maxTempo, state }) {
  const cap = maxTempo === undefined || maxTempo === null || maxTempo === '' ? DEFAULT_MAX_TEMPO : maxTempo;
  if (!(cap in RANK)) throw new Error(`surgicalTempoModule.deriveSurgicalTempo: unknown highest tempo '${cap}' (valid: ${SURGICAL_TEMPOS.join(', ')})`);
  if (!(normalCasesPerTeam > 0)) throw new Error(`surgicalTempoModule.deriveSurgicalTempo: normalCasesPerTeam must be above 0 (got: ${normalCasesPerTeam})`);

  const capacityNormal = Math.max(0, teams) * normalCasesPerTeam;
  const loadRatio = capacityNormal > 0 ? (Math.max(0, backlog) + Math.max(0, demandCases)) / capacityNormal : 0;
  let wanted = 'NORMAL';
  if (loadRatio > LOAD_RATIO_CRISIS_FROM) wanted = 'CRISIS';
  else if (loadRatio > LOAD_RATIO_SURGE_FROM) wanted = 'SURGE';
  const byMascal = tempoOfMascalLevel(mascalLevel);
  if (RANK[byMascal] > RANK[wanted]) wanted = byMascal;
  const candidate = RANK[wanted] <= RANK[cap] ? wanted : cap;

  const prevDays = state && Number.isFinite(state.non_normal_days) ? state.non_normal_days : 0;
  const surgeRec = SURGICAL_FATIGUE_MODEL.SHIFT_RECOMMENDATIONS[TEMPO_RECOMMENDATION_KEY.SURGE];
  const evaluate = (t) => {
    const r = SURGICAL_FATIGUE_MODEL.SHIFT_RECOMMENDATIONS[TEMPO_RECOMMENDATION_KEY[t]];
    // the debt saturates where the performance reaches its floor: further days of overload add nothing to remember
    const days = t === 'NORMAL' ? Math.max(0, prevDays - NORMAL_DAY_RECOVERS_DAYS) : Math.min(MAX_DEBT_DAYS, prevDays + 1);
    const daysOn = t === 'NORMAL' ? surgeRec.days_on_before_rest : r.days_on_before_rest;
    const over = Math.max(0, days - daysOn);
    const debt = Math.max(SLEEP_DEBT_FLOOR, 1 - SLEEP_DEBT_LOSS_PER_DAY * over);
    const shiftPerf = averageShiftPerformance(r.shift_length_hours);
    const perf = shiftPerf * debt;
    return { rec: r, nonNormalDays: days, debt, shiftPerf, perf, perTeam: normalCasesPerTeam * TEMPO_CAPACITY_RATIO[t] * perf };
  };
  let tempo = candidate;
  let e = evaluate(tempo);
  // A worn-out team does less in a surge or crisis tempo than a rested team at the normal tempo: it goes back to the normal
  // tempo for a day (rotation and rest), recovers, and the surge can start again.
  let forcedRest = false;
  if (tempo !== 'NORMAL') {
    const normal = evaluate('NORMAL');
    if (e.perTeam <= normal.perTeam) { tempo = 'NORMAL'; e = normal; forcedRest = true; }
  }
  const rec = e.rec;
  const nonNormalDays = e.nonNormalDays;
  const sleepDebtFactor = e.debt;
  const shiftPerformance = e.shiftPerf;
  const performance = e.perf;

  return {
    tempo,
    wanted_tempo: wanted,
    capped_by_policy: RANK[wanted] > RANK[cap],
    forced_rest: forcedRest,
    load_ratio: Math.round(loadRatio * 100) / 100,
    demand_cases: Math.max(0, demandCases),
    backlog_in: Math.max(0, backlog),
    teams: Math.max(0, teams),
    capacity_cases: Math.max(0, teams) * normalCasesPerTeam * TEMPO_CAPACITY_RATIO[tempo] * performance,
    shift_length_hours: rec.shift_length_hours,
    shifts_per_day: rec.shifts_per_day,
    shift_performance: shiftPerformance,
    sleep_debt_factor: sleepDebtFactor,
    performance,
    capacity_ratio: TEMPO_CAPACITY_RATIO[tempo],
    cases_per_team: normalCasesPerTeam * TEMPO_CAPACITY_RATIO[tempo],
    non_normal_days: nonNormalDays,
    beyond_sustainable_days: tempo !== 'NORMAL' && nonNormalDays > rec.max_duration_days,
    fatigue_mortality_factor: 1 + FATIGUE_MORTALITY_PER_10_PERCENT * ((1 - performance) / 0.10),
    state: { non_normal_days: nonNormalDays }
  };
}
